import React from 'react';
import { Accordion as MAccordion } from '@mui/material';
import { REGISTRATION_CARD_TEXT } from '../../lang';
import {
  useGetAllSlotsApplications,
  type StudentCardCollectionInfo,
  type SlotApplicationsInfo,
} from '../../graphql';
import {
  AssessmentAccordion,
  CustomAccordionSummary,
} from '../../../Assessment';
import { Accordion, Loading, Alert } from '../../../../components';

const groupData = (
  data: StudentCardCollectionInfo[],
  itemsPerGroup: number,
): StudentCardCollectionInfo[][] => {
  const grouped = [];
  for (let i = 0; i < data.length; i += itemsPerGroup) {
    grouped.push(data.slice(i, i + itemsPerGroup));
  }
  return grouped;
};

type TimeSlotProps = {
  slots: SlotApplicationsInfo[];
};

const TimeSlotTable: React.FC<TimeSlotProps> = (props: TimeSlotProps) => {
  const { slots } = props;

  return slots.map((slot, index) => {
    const { slotName, meridiem, quotaLimit, applications } = slot;
    const availableQuota =
      quotaLimit -
      Number(
        applications.filter(application => application.index !== '-').length,
      );
    const maxRowsPerCol = Math.ceil(quotaLimit / 2); // always in 2 columns
    const groupedData = groupData(applications, maxRowsPerCol);
    return (
      <MAccordion
        key={index}
        sx={{
          ':before': { display: 'none' },
          boxShadow: 'none',
          backgroundColor: '#F6F6F6',
          '.MuiButtonBase-root': { minHeight: '0px !important' },
          '.MuiAccordionSummary-content': {
            margin: '0 !important',
          },
          marginBottom: '1rem',
        }}
        defaultExpanded
      >
        <CustomAccordionSummary
          titlecolor="#951d1d"
          sx={{
            width: '100%',
            '.MuiAccordionSummary-content': {
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            },
          }}
        >
          <span className="text-[#951d1d] font-medium">
            <span className="mr-2">{`${meridiem} • ${slotName}`}</span>
          </span>
          <span>
            <span className="font-medium">{REGISTRATION_CARD_TEXT.QUOTA}</span>
            <span className="text-[#951d1d] rounded border-2 border-[#951d1d] px-2 ml-2 bg-[#ffffff]">
              {availableQuota}/{quotaLimit}
            </span>
          </span>
        </CustomAccordionSummary>
        <div className="flex grid grid-cols-2 shadow-sm ml-6 mt-2 mb-3 bg-[#ffffff]">
          {groupedData.map((group, groupIndex) => (
            <div key={groupIndex}>
              <div className="flex grid grid-cols-[1fr,3fr,3fr,3fr] py-3 text-center text-medium bg-[#9B9B9B] text-[#ffffff] text-sm">
                <div>{REGISTRATION_CARD_TEXT.INDEX}</div>
                <div>{REGISTRATION_CARD_TEXT.APP_NO}</div>
                <div>{REGISTRATION_CARD_TEXT.STUDENT_NO}</div>
                <div>{REGISTRATION_CARD_TEXT.STUDENT_NAME}</div>
              </div>
              <div
                className="mb-4 mt-2"
                style={{
                  borderRight:
                    groupIndex < group.length - 1
                      ? '1px solid #EFEFEF'
                      : 'none',
                }}
              >
                {group.map((row, rowIndex) => (
                  <div
                    key={rowIndex}
                    className="flex grid grid-cols-[1fr,3fr,3fr,3fr] text-sm text-center py-1 h-max break-all"
                  >
                    <div className="text-[#951d1d] font-medium">
                      {row.index}
                    </div>
                    <div>{row.applicationNo}</div>
                    <div>{row.studentNumber}</div>
                    <div>{row.studentName}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </MAccordion>
    );
  });
};

export const StudentCardCollection: React.FC = () => {
  const { allSlotsApplications, loading, error } = useGetAllSlotsApplications();

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return loading ? (
    <Loading />
  ) : (
    <Accordion
      title={REGISTRATION_CARD_TEXT.SUBTITLE}
      defaultExpanded
      disableExpand
      sx={{
        backgroundColor: '#F6F6F6',
        '.MuiAccordionSummary-root': {
          backgroundColor: '#F6F6F6',
        },
        '.MuiAccordionDetails-root': { backgroundColor: '#F6F6F6' },
      }}
      details={allSlotsApplications.map((slotApplications, index) => {
        const { date, slots } = slotApplications;

        return (
          <AssessmentAccordion
            key={index}
            title={date}
            defaultExpanded
            AssessDetails={<TimeSlotTable slots={slots} />}
          />
        );
      })}
    />
  );
};
